import React, { useEffect, useState } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, ClipboardEdit, X, FileText, ChevronDown, Loader2 } from 'lucide-react';
import { Table, Tag, Space, Tooltip, Spin, message, Modal, Form, Input, Button } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import type { TableProps, PaginationProps } from 'antd';
import './CallHistory.css';
import axios from '../../config/axios';
import { format } from 'date-fns';
import { templateService } from '../../services/templateService';

interface CallHistoryItem {
  id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  created_at: string;
  duration: number;
  status: string;
  call_type: 'incoming' | 'outgoing';
}

interface PaginationData {
  total: number;
  limit: number;
  offset: number;
}

interface CallHistoryResponse {
  data: CallHistoryItem[];
  pagination: PaginationData;
}

interface VisitDetails {
  title: string;
  templateId: number;
}

interface Template {
  id: number;
  name: string;
}

// Copy button component
const CopySIDButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(true);
        message.success('Call SID copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        message.error('Failed to copy Call SID');
      });
  };
  
  return (
    <Space>
      <Tooltip title={text}>
        <span className="text-xs text-gray-500 font-mono">
          {text.substring(0, 8)}...{text.substring(text.length - 4)}
        </span>
      </Tooltip>
      <Tooltip title="Copy Call SID">
        <button 
          onClick={handleCopy} 
          className="text-blue-500 hover:text-blue-700 focus:outline-none"
          aria-label="Copy Call SID"
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
        </button>
      </Tooltip>
    </Space>
  );
};

export default function CallHistory() {
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCalls, setTotalCalls] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    limit: 5,
    offset: 0
  });
  
  // Scribe related state
  const [isScribing, setIsScribing] = useState(false);
  const [selectedCallSid, setSelectedCallSid] = useState<string | null>(null);
  const [visitDetails, setVisitDetails] = useState<VisitDetails>({
    title: "",
    templateId: 1,
  });
  const [processingScribe, setProcessingScribe] = useState(false);
  const [scribeError, setScribeError] = useState("");
  const [scribeSuccess, setScribeSuccess] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchCallHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/calls/calls?limit=${pagination.limit}&offset=${pagination.offset}`)
    
      if(response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = response.data;
   
    
      setCallHistory(data.data || []);
      setTotalCalls(data.count || 0); // Set the total count from API response
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching call history');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const templates = await templateService.getTemplates();
      setTemplates(templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Error loading templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchCallHistory();
  }, [pagination.limit, pagination.offset]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="success">{status}</Tag>;
      case 'in-progress':
        return <Tag color="processing">{status}</Tag>;
      case 'failed':
        return <Tag color="error">{status}</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const formatPhoneNumber = (number: string | null) => {
    if (!number) return 'Unknown';
    // Clean up the number and format it
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (number.startsWith('+')) {
      // International number
      return number;
    }
    return number;
  };

  const getDirectionIcon = (direction: string | null) => {
    switch (direction) {
      case 'inbound':
        return <PhoneIncoming className="w-4 h-4 text-blue-500" />;
      case 'outbound':
        return <PhoneOutgoing className="w-4 h-4 text-green-500" />;
      default:
        return <Phone className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDateTime = (dateTimeString: string | null) => {
    if (!dateTimeString) return '-';
    
    try {
      const date = new Date(dateTimeString);
      
      // Format date as MM/DD/YYYY
      const formattedDate = date.toLocaleDateString();
      
      // Format time as HH:MM AM/PM
      const formattedTime = date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${formattedDate} ${formattedTime}`;
    } catch (e) {
      return dateTimeString;
    }
  };

  const handlePageChange = (newPage: number) => {
    const newOffset = (newPage - 1) * pagination.limit;
    setPagination({
      ...pagination,
      offset: newOffset,
      total: totalCalls
    });
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: any,
    sorter: any
  ) => {
    const current = pagination.current || 1;
    const pageSize = pagination.pageSize || 10;
    const offset = (current - 1) * pageSize;
    
    setPagination(prevPagination => ({
      ...prevPagination,
      total: totalCalls,
      limit: pageSize,
      offset: offset
    }));
    // fetchCallHistory will be called via useEffect when pagination state changes
  };

  // Function to handle scribe button click
  const handleScribeClick = (callSid: string) => {
    setSelectedCallSid(callSid);
    setIsScribing(true);
    // Set default title
    const callItem = callHistory.find(call => call.call_sid === callSid);
    const defaultTitle = `Call Recording ${formatDateTime(callItem?.created_at || '')}`;
    
    setVisitDetails({
      title: defaultTitle,
      templateId: templates.length > 0 ? templates[0].id : 1,
    });
    
    form.setFieldsValue({
      title: defaultTitle,
      templateId: templates.length > 0 ? templates[0].id : 1
    });
  };

  // Function to close scribe modal
  const closeScribeModal = () => {
    setIsScribing(false);
    setSelectedCallSid(null);
    setVisitDetails({ title: "", templateId: 1 });
    setScribeError("");
    setScribeSuccess(false);
    setProcessingScribe(false);
    setIsTemplateDropdownOpen(false);
    form.resetFields();
  };

  // Function to process scribe
  const processScribe = async () => {
    try {
      const values = await form.validateFields();
      
      if (!selectedCallSid) {
        message.error('No call selected');
        return;
      }

      setProcessingScribe(true);
      setScribeError("");

      try {
        // Step 1: Get call recording details
        const response = await axios.get(`/api/calls/${selectedCallSid}`);

        const callData = response.data;
        
        // Step 2: Process the recording URL
        await axios.post('/api/scribing/speech/process-url', {
          url: callData.recording_url,
          title: values.title,
          template_id: values.templateId
        });

        setScribeSuccess(true);
        message.success('Call recording processed successfully');
        
        // After success, redirect to the visit page
        setTimeout(() => {
          window.open(`${window.location.origin}/visit`, '_blank');
          closeScribeModal();
        }, 2000);
        
      } catch (error) {
        console.error('Error processing scribe:', error);
        setScribeError(error.message || 'An error occurred while processing the recording');
        message.error(error.message || 'An error occurred while processing the recording');
      } finally {
        setProcessingScribe(false);
      }
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'direction',
      key: 'direction',
      render: (direction: string | null) => (
        <Tooltip title={direction === 'inbound' ? 'Incoming Call' : 'Outgoing Call'}>
          {getDirectionIcon(direction)}
        </Tooltip>
      ),
    },
    {
      title: 'From',
      dataIndex: 'from_number',
      key: 'from_number',
      render: (from_number: string | null) => formatPhoneNumber(from_number),
    },
    {
      title: 'To',
      dataIndex: 'to_number',
      key: 'to_number',
      render: (to_number: string | null) => formatPhoneNumber(to_number),
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => (
        <Tooltip title={format(new Date(text), 'PPpp')}>
          <span className="flex items-center">
            <Clock size={16} className="mr-1" />
            {format(new Date(text), 'Pp')}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: string | null) => (
        duration ? (
          <Space>
            <Clock className="w-4 h-4" />
            {duration}s
          </Space>
        ) : '-'
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Call SID',
      dataIndex: 'call_sid',
      key: 'call_sid',
      render: (call_sid: string) => <CopySIDButton text={call_sid} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: CallHistoryItem) => (
        <Space>
          <Tooltip title="Create Scribe">
            <Button 
              type="primary" 
              className="flex items-center" 
              icon={<ClipboardEdit className="mr-1 w-4 h-4" />}
              onClick={() => handleScribeClick(record.call_sid)}
              disabled={record.status !== 'completed'}
            >
              Scribe
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="call-history-container">
      <h2>Call History</h2>
      {error && <div className="error-message">{error}</div>}
      <Table
        columns={columns}
        dataSource={callHistory}
        rowKey={(record) => record.call_sid}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          pageSize: pagination.limit,
          current: Math.floor(pagination.offset / pagination.limit) + 1,
          total: totalCalls,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20'],
          showTotal: (total) => `Total ${total} calls`
        }}
      />

      {/* Scribe Modal */}
      <Modal
        title="Create Scribe"
        open={isScribing}
        onCancel={closeScribeModal}
        footer={null}
        width={500}
        bodyStyle={{ minHeight: 400 }}
      >
        {scribeSuccess ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500">
                <FileText className="w-8 h-8" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-1">Processing Complete!</h3>
            <p className="text-gray-500">Redirecting to visit page...</p>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            initialValues={visitDetails}
            onFinish={processScribe}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Please enter a title for this recording' }]}
            >
              <Input placeholder="Enter a title for this recording" />
            </Form.Item>
            
            <Form.Item
              name="templateId"
              label="Template"
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
                  className="w-full px-4 py-2 text-left text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-blue-500 flex items-center justify-between"
                >
                  <span className="truncate">
                    {visitDetails.templateId
                      ? templates.find(t => t.id === visitDetails.templateId)?.name || "Select Template"
                      : "Select Template"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isTemplateDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isTemplateDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {templatesLoading ? (
                      <div className="px-4 py-2 text-sm text-gray-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading templates...
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        No templates available
                      </div>
                    ) : (
                      templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setVisitDetails({ ...visitDetails, templateId: template.id });
                            form.setFieldsValue({ templateId: template.id });
                            setIsTemplateDropdownOpen(false);
                          }}
                          className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors flex items-center justify-between group"
                        >
                          <span className="font-medium text-gray-900">
                            {template.name}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
            
            {scribeError && (
              <div className="text-red-500 mb-4">
                {scribeError}
              </div>
            )}
            
            <Form.Item>
              <div className="flex justify-end">
                <Button onClick={closeScribeModal} className="mr-2">
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={processingScribe}
                >
                  {processingScribe ? "Processing..." : "Process Recording"}
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
} 